<template>
  <article class="contact-card" :class="{ compact }" :aria-busy="loading">
    <header class="cc-head">
      <div class="cc-avatar" aria-hidden="true">{{ initials }}</div>
      <div class="cc-id">
        <h3 class="cc-name">
          {{ displayName }}
          <span v-if="showIdentifiedBadge" class="cc-ident" :title="`Identifié via : ${identified?.source}`">identifié</span>
        </h3>
        <div v-if="company" class="cc-company" :title="company">
          <Icon name="briefcase" :size="11" />
          <span class="cc-company-name">{{ company }}</span>
        </div>
        <div class="cc-phone" :title="phone">
          <Icon name="phone" :size="11" />
          <span class="mono">{{ formattedPhone }}</span>
        </div>
      </div>
      <span v-if="ticketCount > 0" class="cc-ticket-chip" :title="`${ticketCount} fiche(s) historique`">
        <Icon name="file-text" :size="11" />
        {{ ticketCount }}
      </span>
    </header>

    <div v-if="loading" class="cc-loading">Recherche du contact…</div>

    <div v-else-if="!hasAnyDetails" class="cc-empty">
      <Icon name="info" :size="13" />
      <span>Aucune info supplémentaire pour ce numéro.</span>
    </div>

    <div v-else class="cc-body">
      <!-- Service en cours / dernière demande — visible à tous (contexte
           utile pour personnaliser l'appel) -->
      <div v-if="serviceRequested" class="cc-row cc-row--highlight">
        <Icon name="briefcase" :size="13" class="cc-icon" />
        <div>
          <div class="cc-label">Dernière demande</div>
          <div class="cc-val">{{ serviceRequested }}</div>
        </div>
      </div>

      <!-- Email principal — visible à tous (sera utilisé pour follow-up) -->
      <div v-if="primaryEmail" class="cc-row">
        <Icon name="mail" :size="13" class="cc-icon" />
        <a :href="`mailto:${primaryEmail}`" class="cc-link" target="_blank" rel="noopener">
          {{ primaryEmail }}
        </a>
      </div>

      <!-- ===== Champs avancés — réservés superviseur/admin ============ -->
      <template v-if="!restricted">
        <!-- Adresse -->
        <div v-if="fullAddress" class="cc-row">
          <Icon name="map-pin" :size="13" class="cc-icon" />
          <div>
            <a
              class="cc-link"
              :href="mapLink"
              target="_blank"
              rel="noopener noreferrer"
              :title="`Voir ${fullAddress} sur Google Maps`"
            >
              {{ fullAddress }}
            </a>
          </div>
        </div>

        <!-- Source URL (lien externe — PagesJaunes, etc.) -->
        <div v-if="sourceUrl" class="cc-row">
          <Icon name="globe" :size="13" class="cc-icon" />
          <a :href="sourceUrl" target="_blank" rel="noopener noreferrer" class="cc-link cc-source">
            {{ sourceHost || 'Source' }}
            <Icon name="external-link" :size="10" />
          </a>
        </div>

        <!-- Autres numéros -->
        <div v-if="otherPhones.length" class="cc-row">
          <Icon name="phone-forwarded" :size="13" class="cc-icon" />
          <div class="cc-multi">
            <span class="cc-label">Autres numéros</span>
            <div class="cc-chips">
              <button
                v-for="p in otherPhones"
                :key="p"
                type="button"
                class="cc-phone-chip"
                :title="`Appeler ${p}`"
                @click="$emit('dial', p)"
              >
                <Icon name="phone" :size="10" />
                <span class="mono">{{ p }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Autres emails -->
        <div v-if="otherEmails.length" class="cc-row">
          <Icon name="mail" :size="13" class="cc-icon" />
          <div class="cc-multi">
            <span class="cc-label">Autres emails</span>
            <div class="cc-chips">
              <a
                v-for="e in otherEmails"
                :key="e"
                :href="`mailto:${e}`"
                class="cc-mail-chip"
                target="_blank"
                rel="noopener"
              >{{ e }}</a>
            </div>
          </div>
        </div>
      </template>

      <!-- Traitement du numéro : dernière disposition + nb de tentatives —
           visible à tous (l'agent sait où en est le numéro avant d'appeler). -->
      <div v-if="lastDisposition || attempts > 0" class="cc-row">
        <Icon name="activity" :size="13" class="cc-icon" />
        <div>
          <div class="cc-label">Traitement</div>
          <div class="cc-treat-vals">
            <span v-if="lastDisposition" class="cc-dispo">
              <span class="cc-dispo-dot" :style="{ background: lastDispositionColor || 'var(--text-muted)' }"></span>
              {{ lastDisposition }}
            </span>
            <span v-if="attempts > 0" class="cc-attempts">
              {{ attempts }} tentative{{ attempts > 1 ? 's' : '' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Dernier outcome — visible à tous (contexte d'appel utile) -->
      <div v-if="lastOutcome" class="cc-row">
        <Icon name="check-circle" :size="13" class="cc-icon" />
        <div>
          <div class="cc-label">Dernier résultat</div>
          <div class="cc-val">{{ outcomeLabel }}</div>
        </div>
      </div>

      <!-- Notes — visible à tous (contexte d'appel) -->
      <div v-if="notes" class="cc-row cc-row--notes">
        <Icon name="message-square" :size="13" class="cc-icon" />
        <div>
          <div class="cc-label">Notes précédentes</div>
          <div class="cc-notes">{{ notes }}</div>
        </div>
      </div>

      <!-- Champs custom de l'import CSV — réservés superviseur/admin -->
      <details v-if="!restricted && extrasEntries.length" class="cc-extras">
        <summary>
          <Icon name="more-horizontal" :size="13" />
          <span>{{ extrasEntries.length }} info{{ extrasEntries.length > 1 ? 's' : '' }} additionnelle{{ extrasEntries.length > 1 ? 's' : '' }}</span>
        </summary>
        <dl class="cc-extras-list">
          <template v-for="[k, v] in extrasEntries" :key="k">
            <dt>{{ formatKey(k) }}</dt>
            <dd>{{ v }}</dd>
          </template>
        </dl>
      </details>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from './Icon.vue'
import { OUTCOME_LABELS, type ContactLookup, type Outcome } from '@/shared/api/queue'

const props = withDefaults(
  defineProps<{
    /** Données du lookup serveur (queue + tickets). */
    lookup: ContactLookup | null
    /** Téléphone brut affiché (utilisé en repli si lookup vide). */
    phone: string
    /** Nom affiché si lookup vide (ex. depuis le CDR entrant). */
    fallbackName?: string
    loading?: boolean
    /** Si vrai, mode dense (padding réduit) pour intégration en sidebar étroite. */
    compact?: boolean
    /** Si vrai, masque les données sensibles (adresse, source URL, emails
     *  alternatifs, champs custom de l'import). À utiliser pour les agents
     *  qui n'ont pas besoin d'accéder au lead complet. Owner/admin voient
     *  tout par défaut (`restricted=false`).
     */
    restricted?: boolean
  }>(),
  { fallbackName: '', loading: false, compact: false, restricted: false },
)

defineEmits<{ (e: 'dial', phone: string): void }>()

const queueEntry = computed(() => props.lookup?.queue_entry ?? null)
const ticketCount = computed(() => props.lookup?.ticket_count ?? 0)

// Identification par croisement de nos données (carnet, autres campagnes, RDV)
// — sert de repli quand la fiche en cours n'a pas de nom/entreprise.
const identified = computed(() => props.lookup?.identified ?? null)
// Badge « identifié » : le nom/entreprise affiché vient du croisement (la fiche
// elle-même n'avait pas l'info).
const showIdentifiedBadge = computed(
  () => !!identified.value?.source
    && (!queueEntry.value?.name || !queueEntry.value?.company),
)

const displayName = computed(
  () => queueEntry.value?.name || identified.value?.name
    || props.fallbackName || 'Contact inconnu',
)

// Nom d'entreprise — colonne structurée, avec repli sur custom_data (imports
// historiques) puis sur l'identification croisée.
const company = computed<string>(
  () => queueEntry.value?.company || queueEntry.value?.custom_data?.company
    || identified.value?.company || '',
)

const initials = computed(() => {
  const n = displayName.value.trim()
  if (!n || n === 'Contact inconnu') return '?'
  return n
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
})

const formattedPhone = computed(() => {
  // Format E.164 → "+1 (438) 792-1464" pour Amérique du Nord ; sinon brut.
  const p = props.phone || queueEntry.value?.phone || ''
  if (/^\+1\d{10}$/.test(p)) {
    return `+1 (${p.slice(2, 5)}) ${p.slice(5, 8)}-${p.slice(8)}`
  }
  if (/^\+33\d{9}$/.test(p)) {
    return `+33 ${p[3]} ${p.slice(4, 6)} ${p.slice(6, 8)} ${p.slice(8, 10)} ${p.slice(10)}`
  }
  return p
})

const customData = computed(() => queueEntry.value?.custom_data || {})

const serviceRequested = computed(() => queueEntry.value?.service_requested || '')
const notes = computed(() => queueEntry.value?.notes || '')
const lastOutcome = computed(() => queueEntry.value?.last_outcome || '')
const outcomeLabel = computed(() =>
  lastOutcome.value ? OUTCOME_LABELS[lastOutcome.value as Outcome] : '',
)
// Traitement du numéro : dernière disposition (nom + couleur) + tentatives.
const lastDisposition = computed(() => queueEntry.value?.last_disposition_name || '')
const lastDispositionColor = computed(() => queueEntry.value?.last_disposition_color || '')
const attempts = computed(() => queueEntry.value?.call_attempts ?? 0)

const primaryEmail = computed<string>(() => customData.value.email || '')
const otherEmails = computed<string[]>(() => {
  const all = customData.value.all_emails || []
  return all.filter((e: string) => e && e !== primaryEmail.value)
})
const otherPhones = computed<string[]>(() => {
  const all = customData.value.all_phones || []
  return all.filter((p: string) => p && p !== props.phone)
})

const fullAddress = computed(() => {
  const a = customData.value.address || ''
  const c = customData.value.city || ''
  if (a && c && !a.toLowerCase().includes(c.toLowerCase())) return `${a}, ${c}`
  return a || c
})
const mapLink = computed(() =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress.value)}`,
)
const sourceUrl = computed<string>(() => customData.value.source_url || '')
const sourceHost = computed<string>(() => customData.value.source_host || '')

const extrasEntries = computed<[string, string][]>(() => {
  const e = customData.value.extras || {}
  return Object.entries(e) as [string, string][]
})

const hasAnyDetails = computed(() => {
  // Toujours visible
  if (
    primaryEmail.value ||
    notes.value ||
    lastOutcome.value ||
    serviceRequested.value ||
    lastDisposition.value ||
    attempts.value > 0
  ) return true
  // Visible uniquement hors mode restreint
  if (props.restricted) return false
  return !!(
    fullAddress.value ||
    sourceUrl.value ||
    otherEmails.value.length ||
    otherPhones.value.length ||
    extrasEntries.value.length
  )
})

function formatKey(k: string): string {
  return k
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
</script>

<style scoped>
.contact-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
  color: var(--text);
}
.contact-card.compact { padding: 10px 12px; gap: 8px; }

.cc-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cc-avatar {
  width: 38px; height: 38px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-pressed) 100%);
  color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700;
  flex-shrink: 0;
}
.cc-id { flex: 1; min-width: 0; }
.cc-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cc-ident {
  display: inline-block; margin-left: 6px; vertical-align: middle;
  font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
  color: var(--info); background: var(--info-bg, rgba(56,139,253,0.12));
  border-radius: 8px; padding: 1px 6px;
}
.cc-company {
  display: flex; align-items: center; gap: 5px;
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 12.5px;
  font-weight: 600;
  min-width: 0;
}
.cc-company-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cc-company .ti, .cc-company svg { color: var(--text-muted); flex-shrink: 0; }
.cc-phone {
  display: flex; align-items: center; gap: 5px;
  margin-top: 2px;
  color: var(--text-muted);
  font-size: 12px;
}
.cc-ticket-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  background: var(--primary-100);
  color: var(--primary);
  border-radius: var(--radius-full);
  font-size: 11.5px;
  font-weight: 600;
}

.cc-loading, .cc-empty {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 4px;
  color: var(--text-muted);
  font-size: 12.5px;
}

.cc-body { display: flex; flex-direction: column; gap: 8px; }

.cc-row {
  display: flex; align-items: flex-start; gap: 9px;
  padding: 6px 0;
  border-top: 1px solid var(--border);
}
.cc-row:first-child { border-top: none; padding-top: 2px; }
.cc-row--highlight {
  background: var(--info-bg);
  border-radius: var(--radius-sm);
  border: 1px solid var(--info-border);
  padding: 8px 10px;
}
.cc-row--highlight .cc-icon { color: var(--info); }
.cc-row--notes .cc-notes {
  font-size: 12.5px;
  color: var(--text-secondary);
  white-space: pre-wrap;
  line-height: 1.45;
}

.cc-icon { color: var(--text-muted); flex-shrink: 0; margin-top: 2px; }
.cc-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.cc-val { font-size: 13px; color: var(--text); font-weight: 500; }

.cc-treat-vals { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.cc-dispo {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 12px; font-weight: 600; color: var(--text);
}
.cc-dispo-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cc-attempts {
  font-size: 11.5px; color: var(--text-muted);
  background: var(--surface-hover);
  border-radius: var(--radius-full);
  padding: 2px 8px;
}

.cc-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  word-break: break-word;
}
.cc-link:hover { text-decoration: underline; }
.cc-source {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12.5px;
}

.cc-multi { flex: 1; min-width: 0; }
.cc-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.cc-phone-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 11.5px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--duration-fast);
}
.cc-phone-chip:hover {
  background: var(--primary-100);
  border-color: var(--primary);
  color: var(--primary);
}
.cc-mail-chip {
  display: inline-block;
  padding: 3px 8px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 11.5px;
  color: var(--text-secondary);
  text-decoration: none;
  word-break: break-all;
}
.cc-mail-chip:hover { color: var(--primary); border-color: var(--primary); }

.mono { font-family: var(--font-mono); }

.cc-extras {
  border-top: 1px solid var(--border);
  padding-top: 8px;
}
.cc-extras summary {
  display: flex; align-items: center; gap: 6px;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
  list-style: none;
  padding: 2px 0;
}
.cc-extras summary::-webkit-details-marker { display: none; }
.cc-extras summary:hover { color: var(--text); }
.cc-extras[open] summary { color: var(--text); margin-bottom: 6px; }
.cc-extras-list {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 12px;
  margin: 0;
  font-size: 12px;
}
.cc-extras-list dt {
  font-weight: 600;
  color: var(--text-muted);
  text-transform: capitalize;
}
.cc-extras-list dd {
  margin: 0;
  color: var(--text);
  word-break: break-word;
}
</style>
